import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { SearchInputOption } from "../../../shared/components/search-bar/search-input/search-input.component";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { BulkUrlType } from "../../../shared/types/bulk-upload/bulk-url.type";
import { CompanyAttachment } from "../company-attachments/company-attachment.type";
import { User } from "../users/user.type";
import { Company } from "./company.type";

@Injectable({
  providedIn: "root",
})
export class CompanyService {
  basePath = `${environment.serverUrl}/companies`;
  basePathCsvQueue = `${this.basePath}/queue-csv`;

  constructor(private http: HttpClient) {}

  getCompanies(options?: QueryParams<Company>): Observable<PaginatedResponse<Company>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<Company>>(this.basePath, { params }).pipe(
      tap((_data: any) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getCompaniesPost(options?: QueryParams<Company>): Observable<PaginatedResponse<Company>> {
    const body = { ...DEFAULT_QUERY_PARAMS, ...options };
    return this.http.post<PaginatedResponse<Company>>(`${this.basePath}/get-many`, body).pipe(
      tap((_data: any) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getCompany(companyId: string, options?: QueryParams<Company>): Observable<Company> {
    let params: HttpParams = new HttpParams();
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<Company>(`${this.basePath}/${companyId}`, { params }).pipe(
      tap((_data: Company) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  createCompany(data): Observable<Company> {
    return this.http.post<Company>(this.basePath, data).pipe(
      tap((_data: Company) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateCompany(id, data): Observable<Company> {
    return this.http.put<Company>(`${this.basePath}/${id}`, data).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  deleteCompany(id: string): Observable<Company> {
    return this.http.delete<Company>(`${this.basePath}/${id}`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  uploadBatch(url: string, csvFile: File) {
    return this.http.put<any>(url, csvFile);
  }

  getSearchableCompany(options?: QueryParams<Company>): Observable<SearchInputOption[]> {
    return this.getCompanies(options).pipe(
      map((r: PaginatedResponse<Company>) =>
        r.items.map((company: Company) => ({
          auxValue: company.id,
          value: company.name,
          label: company.name,
        })),
      ),
    );
  }

  getPutSignedUrl(): Observable<BulkUrlType> {
    return this.http.get<any>(`${this.basePath}/batchs/bulk-upload-url`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getCompaniesQueue(options?: QueryParams<Company>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getCompanyAttachments(
    companyId: string,
    options?: QueryParams<CompanyAttachment>,
  ): Observable<PaginatedResponse<CompanyAttachment>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http
      .get<PaginatedResponse<CompanyAttachment>>(`${this.basePath}/${companyId}/company-attachments`, {
        params,
      })
      .pipe(
        tap((_data) => console.log()),
        catchError(this.handleError<any>("")),
      );
  }

  getS3Url(companyId: string, extension: string): Observable<{ url: string; name: string }> {
    return this.http.get(`${this.basePath}/${companyId}/url-company-attachments`, { params: { extension } }).pipe(
      tap((_data: CompanyAttachment) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  createAttachment(
    company: Pick<CompanyAttachment, "name" | "url" | "description" | "companyId">,
  ): Observable<CompanyAttachment> {
    return this.http
      .post<Observable<CompanyAttachment>>(`${this.basePath}/${company.companyId}/company-attachments`, company)
      .pipe(
        tap(() => console.log()),
        catchError(this.handleError<any>("")),
      );
  }

  updateAttachment(
    companyId: string,
    companyAttachmentId: string,
    data: Partial<CompanyAttachment>,
  ): Observable<CompanyAttachment> {
    return this.http
      .put<Company>(`${this.basePath}/${companyId}/company-attachments/${companyAttachmentId}`, data)
      .pipe(
        tap((_data: Company) => console.log()),
        catchError(this.handleError<any>("")),
      );
  }

  getAvailableUsersToLink(companyId: string, options?: QueryParams<User>): Observable<PaginatedResponse<User>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http
      .get<PaginatedResponse<User>>(`${this.basePath}/${companyId}/available-users-to-link`, { params })
      .pipe(catchError(this.handleError<any>()));
  }

  linkUsers(id: string, data): Observable<void> {
    return this.http.post<void>(`${this.basePath}/${id}/users`, data).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return throwError(error);
    };
  }
}
